import { IconButton, useMediaQuery } from '@mui/material';
import { SafeMarkdown } from '../markdown/safe';
import { Text } from '../text';
import { Buttons, KpiContainer, KpiHeader, KpiHeaderContent } from './styles';
import { ChevronLeft, ChevronRight, InfoOutlined } from '@mui/icons-material';
import { VariableInfoTooltip } from '@/components/molecules/variableInfo';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';

interface KpiProps {
  title: string;
  value: string | number | number[] | string[];
  tooltip?: {
    index: number;
    title: string;
    description: string;
    source: string;
    period: string;
  };
  openTooltipIndex: number | null;
  onTooltipToggle: (index: number) => void;
  onTooltipClose: () => void;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  onOpenModal: () => void;
}

export const Kpi = ({
  title,
  value,
  tooltip,
  openTooltipIndex,
  onTooltipToggle,
  onTooltipClose,
  placement = 'top',
  onOpenModal,
}: KpiProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXmd = useMediaQuery(theme.breakpoints.down('xmd'));

  const isNumber = typeof value === 'number';
  const isArray = Array.isArray(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset index when value changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [value]);

  // Fechar tooltip ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        tooltip &&
        openTooltipIndex === tooltip.index
      ) {
        onTooltipClose();
      }
    };

    if (tooltip && openTooltipIndex === tooltip.index) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openTooltipIndex, tooltip, onTooltipClose]);

  const handlePrevious = () => {
    if (!isArray || !Array.isArray(value)) return;
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (!isArray || !Array.isArray(value)) return;
    setCurrentIndex((prev) => Math.min(value.length - 1, prev + 1));
  };

  const isFirstValue = currentIndex === 0;
  const isLastValue = isArray && Array.isArray(value) && currentIndex === value.length - 1;

  const getDisplayValue = () => {
    if (isNumber) return value;
    if (isArray && Array.isArray(value) && value.length > 0) {
      return value[currentIndex];
    }
    return value;
  };

  const displayValue = getDisplayValue();

  return (
    <KpiContainer variant="outlined" ref={containerRef}>
      <KpiHeader>
        <KpiHeaderContent>
          <Text variant="body2" color="text.secondary" sx={{ width: 'fit-content' }}>
            <SafeMarkdown text={title} />
          </Text>

          {tooltip && (
            <VariableInfoTooltip
              description={tooltip.description}
              source={tooltip.source}
              period={tooltip.period}
              open={openTooltipIndex === tooltip.index}
              onClose={onTooltipClose}
              width="300px"
              titleWidth="80px"
              titleMinWidth="auto"
              valueWidth="fit-content"
              placement={placement}
            >
              <IconButton
                sx={{ padding: 0 }}
                onClick={() => (isMobile ? onOpenModal() : onTooltipToggle(tooltip.index))}
              >
                <InfoOutlined color="action" sx={{ fontSize: 22 }} />
              </IconButton>
            </VariableInfoTooltip>
          )}
        </KpiHeaderContent>

        {isArray && Array.isArray(value) && value.length > 1 && (
          <Buttons>
            <IconButton
              onClick={handlePrevious}
              disabled={isFirstValue}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <ChevronLeft color={isFirstValue ? 'disabled' : 'primary'} />
            </IconButton>

            <IconButton
              onClick={handleNext}
              disabled={isLastValue}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <ChevronRight color={isLastValue ? 'disabled' : 'primary'} />
            </IconButton>
          </Buttons>
        )}
      </KpiHeader>

      <Text variant={'h6'} color="text.primary" sx={{ width: isXmd ? '180px' : 'fit-content' }}>
        {typeof displayValue === 'number' ? (
          displayValue
        ) : (
          <SafeMarkdown text={displayValue as string} />
        )}
      </Text>
    </KpiContainer>
  );
};
